/* MIT License

Copyright (c) 2025 Brad Schick

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE. */
import { getSodium } from './crypto';
import * as cc from './cipher.consts';

import {
   getRandom,
   numToBytes,
   bytesToNum,
   BYOBStreamReader,
   bytesFromUTF8String,
   getArrayBuffer,
   ensureArrayBuffer,
   clamp,
   concatArrays,
   logError,
} from './utils';
import { type KeyProvider, PWDKeyProvider } from './keys';

export enum CipherState {
   Error,
   Initialized,
   Block0Decoded,
   Block0Done,
   Finished,
}

export type CipherDataBlock = {
   readonly parts: Uint8Array[];
   readonly state: CipherState;
};

export type ReadOpts = {
   startSize?: number;
   maxSize?: number;
};

export type CipherDataInfo = {
   readonly ver: number;
   readonly alg: cc.CipherAlgs;
   readonly lp: number;
   readonly lpEnd: number;
   readonly slt: Uint8Array<ArrayBuffer>;
   readonly ic: number;
   hint?: string | undefined;
};

export abstract class Ciphers {
   protected _state: CipherState;
   protected _multiBlock = false;

   protected constructor(
      protected _keyProvider: KeyProvider,
      protected _reader: BYOBStreamReader,
   ) {
      this._state = CipherState.Initialized;
   }

   public get multiBlock(): boolean {
      if (this._state !== CipherState.Finished) {
         throw new Error(`Cipher not finished ${this._state}`);
      }
      return this._multiBlock;
   }

   public finishedState() {
      this._state = CipherState.Finished;
      this._purge();
   }

   public errorState() {
      this._state = CipherState.Error;
      this._purge();
   }

   protected _purge() {
      this._keyProvider.purge();
      this._reader.cleanup();
   }

   public abstract protocolVersion(): number;

   static async benchmark(
      testSize: number,
      targetMillis: number,
      maxMillis: number,
   ): Promise<[number, number, number]> {
      const keyProvider = new PWDKeyProvider(crypto.getRandomValues(new Uint8Array(32)), ['AVeryBogusPwd', '']);
      keyProvider.setCipherDataInfo({
         ver: cc.CURRENT_VERSION,
         alg: 'AES-GCM',
         ic: testSize,
         lp: 1,
         lpEnd: 1,
         slt: crypto.getRandomValues(new Uint8Array(cc.SLT_BYTES)),
      });

      const start = Date.now();
      await keyProvider.getCipherKey(true);
      const test_millis = Date.now() - start;
      keyProvider.purge();

      const hashRate = testSize / test_millis;

      // Don't allow more then ~5 minutes of pwd hashing (rounded to millions)
      const iCountMax = Math.min(cc.ICOUNT_MAX, Math.round((maxMillis * hashRate) / 1000000) * 1000000);

      const targetICount = Math.round((hashRate * targetMillis) / 100000) * 100000;
      // Add ICOUNT_MIN to calculated target because benchmark is done during
      // page load and tends to be too low.
      const iCount = Math.max(cc.ICOUNT_DEFAULT, targetICount + cc.ICOUNT_MIN);

      console.debug(
         `bench: ${testSize}i, in: ${test_millis}ms, rate: ${Math.round(hashRate)}i/ms,
        ic: ${iCount}i, icm: ${iCountMax}i`,
      );

      return [iCount, iCountMax, hashRate];
   }

   static isValidAlg(alg: string): alg is cc.CipherAlgs {
      return Object.keys(cc.AlgInfo).includes(alg);
   }

   static validateAlg(alg: string): cc.CipherAlgs {
      if (!Ciphers.isValidAlg(alg)) {
         throw new Error(`Unsupported cipher mode: ${alg}.`);
      }
      return alg;
   }

   static validateAlgs(algs: string[]): cc.CipherAlgs[] {
      return algs.map((alg) => Ciphers.validateAlg(alg));
   }

   static algName(algId: number): cc.CipherAlgs {
      for (const alg of Ciphers.algs()) {
         if (cc.AlgInfo[alg].id === algId) {
            return alg;
         }
      }
      throw new Error(`Unsupported cipher mode: ${algId}`);
   }

   static algId(alg: cc.CipherAlgs): number {
      return cc.AlgInfo[alg].id;
   }

   static algIVByteLength(alg: cc.CipherAlgs): number {
      return cc.AlgInfo[alg].ivBytes;
   }

   static algs(): cc.CipherAlgs[] {
      return Object.keys(cc.AlgInfo) as cc.CipherAlgs[];
   }

   static algDescription(alg: string): string {
      return Ciphers.isValidAlg(alg) ? cc.AlgInfo[alg].description : 'Invalid';
   }

   static getICountMin(ver: number): number {
      return ver < cc.VERSION5 ? cc.ICOUNT_MIN_V4 : cc.ICOUNT_MIN;
   }

   // Only useful for validating params before encoding. Decoded values are read with
   // expected sizes, so validity depends on signature validate rather than decoded lengths
   public static validateAdditionalData(args: {
      alg: cc.CipherAlgs;
      iv: Uint8Array;
      term?: boolean;
      ic?: number;
      slt?: Uint8Array;
      lp?: number;
      lpEnd?: number;
      ver?: number;
      encryptedHint?: Uint8Array;
   }) {
      Ciphers.validateAlg(args.alg);
      const ivBytes = Number(Ciphers.algIVByteLength(args.alg));
      if (args.iv.byteLength !== ivBytes) {
         throw new Error(`Invalid iv size: ${args.iv.byteLength}`);
      }

      if (!args.slt) {
         // If there is no salt, there should be no ic
         if (args.ic !== undefined) {
            throw new Error('Unexpected ic with slt');
         }
      } else {
         if (args.slt.byteLength !== cc.SLT_BYTES) {
            throw new Error(`Invalid slt len: ${args.slt.byteLength}`);
         }
         // Sanity range only. The version-aware floor is applied by the key provider
         if (args.ic === undefined || (args.ic !== 0 && (args.ic < cc.ICOUNT_MIN_V4 || args.ic > cc.ICOUNT_MAX))) {
            throw new Error(`Invalid ic: ${args.ic}`);
         }
      }

      if (args.lpEnd) {
         if (!args.lp) {
            throw new Error('Missing lp');
         }
         if (args.lpEnd < 1 || args.lpEnd > cc.LP_MAX) {
            throw new Error(`Invalid lpEnd: ${args.lpEnd}`);
         }
         if (args.lp < 1 || args.lp > args.lpEnd) {
            throw new Error(`Invalid lp: ${args.lp}`);
         }
      } else if (args.lp) {
         throw new Error('Missing lpEnd');
      }

      // Could move this to subclasses...
      if (args.ver && args.ver !== cc.VERSION1) {
         // Only V1 put version in additional data
         throw new Error(`Unexpected version: ${args.ver}`);
      }
      if (args.term !== undefined && args.ver && args.ver < cc.VERSION6) {
         // Only V6+ put term in additional data
         throw new Error('Unexpected term flag');
      }

      if (args.ver && args.lp) {
         throw new Error('Unexpected version and lp');
      }

      if (args.encryptedHint && args.encryptedHint.byteLength > cc.ENCRYPTED_HINT_MAX_BYTES) {
         throw new Error(`Invalid encrypted hint length: ${args.encryptedHint.byteLength}`);
      }
   }

   protected static _encodeAD(args: {
      alg: cc.CipherAlgs;
      iv: Uint8Array;
      term?: boolean;
      ic?: number;
      slt?: Uint8Array;
      lp?: number;
      lpEnd?: number;
      ver?: number;
      encryptedHint?: Uint8Array;
      keyCommitment?: Uint8Array;
   }): Uint8Array<ArrayBuffer> {
      Ciphers.validateAdditionalData(args);

      // Packer validates ranges as values are added
      const packer = new Packer(cc.ADDIONTAL_DATA_MAX_BYTES);

      // Will be a bitfield when more flags are needed
      if (args.term !== undefined) {
         packer.flags = args.term ? 1 : 0;
      }
      packer.alg = args.alg;
      packer.iv = args.iv;

      if (args.slt) {
         packer.slt = args.slt;
      }

      if (args.ic !== undefined) {
         packer.ic = args.ic;
      }

      if (args.lp && args.lpEnd) {
         packer.lpp(args.lp, args.lpEnd);
      }

      if (args.ver) {
         packer.ver = args.ver;
      }

      if (args.encryptedHint !== undefined) {
         packer.hint = args.encryptedHint;
      }

      if (args.keyCommitment !== undefined) {
         packer.commit = args.keyCommitment;
      }

      return packer.trim();
   }
}

export abstract class Encipher extends Ciphers {
   // Simple perf testing with Chrome 126 on MacOS result in
   // readAvailable with READ_SIZE_MAX of 4x to be the fastest
   protected static readonly READ_SIZE_START = 1048576; // 1 MiB
   protected static readonly READ_SIZE_MAX = Encipher.READ_SIZE_START * 4;

   protected constructor(keyProvider: KeyProvider, reader: BYOBStreamReader) {
      super(keyProvider, reader);
   }

   // The encryptBlock functions return CipherDataBlock instead of
   // an opaque byte array to reduce copying (the caller must write
   // all the blocks in order)
   async encryptBlock(): Promise<CipherDataBlock> {
      if (this._state === CipherState.Initialized) {
         return this.encryptBlock0();
      } else if (this._state === CipherState.Block0Done) {
         return this.encryptBlockN();
      } else {
         throw new Error(`Encipher invalid state ${this._state}`);
      }
   }

   public abstract encryptBlock0(): Promise<CipherDataBlock>;
   public abstract encryptBlockN(): Promise<CipherDataBlock>;
}

// (exported for testing)
export class EncipherV8 extends Encipher {
   /* CipherData Layout. Tags are just notation, and are not actually in the
    * data stream. All encodings have one block0 instance followed by zero or
    * more blockN instances

      <Document>
         <Block0>
            <Header>
               MAC_BYTES - 32
               VER_BYTES - 2
               PAYLOAD_SIZE_BYTES - 3
            </Header>
            <Payload>
               <Additional Data>
                  FLAGS_BYTES - 1
                  ALG_BYTES - 2
                  IV_BYTES (variable) - [12, 24, 32]
                  SLT_BYTES - 16
                  IC_BYTES - 4
                  LPP_BYTES (packed lp and lpEnd) - 1
                  EHINT_LEN_BYTES - 1
                  EHINT_BYTES (variable) - [0-255]
                  COMMIT_LEN_BYTES - 1
                  COMMIT_BYTES (variable) - [0, 32]
               </Additional Data>
               <Encrypted Data>
                  EDATA_BYTES (variable)
               </Encrypted Data>
            </Payload>
         </Block0>
         <BlockN>
            <Header>
               MAC_BYTES
               VER_BYTES
               PAYLOAD_SIZE_BYTES
            </Header>
            <Payload>
               <Additional Data>
                  FLAGS_BYTES
                  ALG_BYTES
                  IV_BYTES (variable)
               </Additional Data>
               <Encrypted Data>
                  EDATA_BYTES (variable)
               </Encrypted Data>
            </Payload>
         </BlockN>
         ...
      </Document>
   */

   private _blockNum: number;
   private _readTarget: number;
   private readonly _readSizeMax: number;
   private _lastMac?: Uint8Array;

   constructor(keyProvider: KeyProvider, reader: BYOBStreamReader, readOpts?: ReadOpts) {
      super(keyProvider, reader);

      // Assign in ctor to allow for monkey patching in tests
      this._readTarget = clamp(readOpts?.startSize ?? Encipher.READ_SIZE_START, 1, Encipher.READ_SIZE_MAX);
      this._readSizeMax = clamp(readOpts?.maxSize ?? Encipher.READ_SIZE_MAX, 1, Encipher.READ_SIZE_MAX);
      this._blockNum = 1;
      this._lastMac = new Uint8Array([0]);
   }

   protected override _purge() {
      if (this._lastMac) {
         this._lastMac.fill(0);
         this._lastMac = undefined;
      }
      super._purge();
   }

   public override protocolVersion(): number {
      return cc.VERSION8;
   }

   // Overall order of operations for encryption
   //
   // 1. Generate new salt and iv/nonce values
   // 2. Generate keys
   // 3. Encrypt hint if it exists
   // 2. Encode cipher parameters as additional data
   // 4. Encrypt cleartext using cipher key (with addition data)
   // 5. Sign addtional data + cipher text with signing key
   // 6. Return all the parts of the cipherdata
   //
   override async encryptBlock0(): Promise<CipherDataBlock> {
      try {
         if (this._state !== CipherState.Initialized) {
            throw new Error(`Encipher invalid state ${this._state}`);
         }

         const [clearBuffer, done] = await this._reader.readAvailable(new ArrayBuffer(this._readTarget));

         if (clearBuffer.byteLength === 0) {
            if (done) {
               // must always have a block0
               throw new Error('Missing clear data');
            }
            // This can happen when the stream isn't done but return no data
            // callers should call encryptBlock0 again
            this._state = CipherState.Initialized;

            return {
               parts: [],
               state: this._state,
            };
         }

         let cdInfo = this._keyProvider.getCipherDataInfo();
         const iv = getRandom(Number(Ciphers.algIVByteLength(cdInfo.alg)));
         const ek = await this._keyProvider.getCipherKey(true);
         cdInfo = this._keyProvider.getCipherDataInfo();

         let encryptedHint: Uint8Array<ArrayBufferLike> = new Uint8Array(0);
         if (cdInfo.hint) {
            const maxHintBytes = cc.ENCRYPTED_HINT_MAX_BYTES - cc.AUTH_TAG_MAX_BYTES;
            const hintBytes = bytesFromUTF8String(cdInfo.hint, maxHintBytes);

            // 0xFF cannot appear in UTF-8, so trailing pad is distinguishable from hint text
            const paddedLen = Math.min(
               Math.ceil(hintBytes.byteLength / cc.HINT_LEN_MODULUS) * cc.HINT_LEN_MODULUS,
               maxHintBytes,
            );
            const paddedHint = new Uint8Array(paddedLen).fill(0xff);
            paddedHint.set(hintBytes);

            try {
               const [hk, hIV] = await this._keyProvider.getHintCipherKeyAndIV(iv);
               encryptedHint = await EncipherV8._doEncrypt(cdInfo.alg, hk, hIV, paddedHint);
            } finally {
               paddedHint.fill(0);
            }
         }

         const keyCommitment = this._keyProvider.supportsCommitment
            ? await this._keyProvider.getKeyCommitment()
            : new Uint8Array(0);

         const aeadAD = Ciphers._encodeAD({
            alg: cdInfo.alg,
            iv,
            term: done,
            ic: cdInfo.ic,
            lp: cdInfo.lp,
            lpEnd: cdInfo.lpEnd,
            slt: cdInfo.slt,
            encryptedHint,
            keyCommitment,
         });

         // Only block0 uses the root cipher key. Simplifies backward compat and is no less secure
         const encryptedData = await EncipherV8._doEncrypt(cdInfo.alg, ek, iv, clearBuffer, aeadAD);

         const headerData = await this._createHeader(encryptedData, aeadAD);

         if (done) {
            this.finishedState();
         } else {
            this._state = CipherState.Block0Done;
         }

         return {
            parts: [headerData, aeadAD, encryptedData],
            state: this._state,
         };
      } catch (err) {
         this.errorState();
         logError(err);
         throw err;
      }
   }

   override async encryptBlockN(): Promise<CipherDataBlock> {
      try {
         if (this._state !== CipherState.Block0Done) {
            throw new Error(`Encipher invalid state ${this._state}`);
         }

         this._multiBlock = true;

         this._readTarget = Math.min(this._readTarget * 2, this._readSizeMax);
         let clearBuffer: Uint8Array;
         let done: boolean;

         while (true) {
            [clearBuffer, done] = await this._reader.readAvailable(new ArrayBuffer(this._readTarget));

            // There can be read stalls, if so we keep trying. If we received data or
            // are done, exist loop. Note that we can be done and get zero data due to
            // the previous read matching exactly remaining data. In that case, we append
            // another block that won't produce clearText (but is a valid term)
            if (clearBuffer.byteLength !== 0 || done) {
               break;
            }
         }

         const cdInfo = this._keyProvider.getCipherDataInfo();
         const iv = getRandom(Number(Ciphers.algIVByteLength(cdInfo.alg)));
         const bk = await this._keyProvider.getBlockCipherKey(this._blockNum);
         this._blockNum += 1;

         const aeadAD = Ciphers._encodeAD({
            alg: cdInfo.alg,
            iv,
            term: done,
         });

         const encryptedData = await EncipherV8._doEncrypt(cdInfo.alg, bk, iv, clearBuffer, aeadAD);
         const headerData = await this._createHeader(encryptedData, aeadAD);

         if (done) {
            this.finishedState();
         }

         return {
            parts: [headerData, aeadAD, encryptedData],
            state: this._state,
         };
      } catch (err) {
         this.errorState();
         logError(err);
         throw err;
      }
   }

   // Helper function
   protected static async _doEncrypt(
      alg: cc.CipherAlgs,
      key: Uint8Array,
      iv: Uint8Array,
      clear: Uint8Array,
      additionalData?: Uint8Array,
   ): Promise<Uint8Array> {
      const ivBytes = Number(Ciphers.algIVByteLength(alg));
      if (ivBytes !== iv.byteLength) {
         throw new Error(`incorrect iv length of: ${iv.byteLength}`);
      }
      if (key.every((b) => b === 0)) {
         throw new Error('Invalid key: all zero bytes');
      }

      let encryptedBytes: Uint8Array;
      if (alg === 'X20-PLY') {
         try {
            encryptedBytes = getSodium().crypto_aead_xchacha20poly1305_ietf_encrypt(
               clear,
               additionalData ?? null,
               null,
               iv,
               key,
            );
         } catch (err) {
            // Match behavior of Web Crytpo functions that throws limited DOMException
            const msg = err instanceof Error ? err.message : '';
            throw new DOMException(msg, 'OperationError ');
         }
      } else if (alg === 'AEGIS-256') {
         try {
            encryptedBytes = getSodium().crypto_aead_aegis256_encrypt(clear, additionalData ?? null, null, iv, key);
         } catch (err) {
            // Match behavior of Web Crytpo functions that throws limited DOMException
            const msg = err instanceof Error ? err.message : '';
            throw new DOMException(msg, 'OperationError ');
         }
      } else {
         let ek: CryptoKey | undefined = await crypto.subtle.importKey('raw', getArrayBuffer(key), 'AES-GCM', false, [
            'encrypt',
         ]);

         const buffer = await crypto.subtle.encrypt(
            {
               name: alg,
               iv: getArrayBuffer(iv),
               additionalData: additionalData ? getArrayBuffer(additionalData) : new ArrayBuffer(0),
               tagLength: cc.AES_GCM_TAG_BYTES * 8,
            },
            ek,
            getArrayBuffer(clear),
         );

         ek = undefined;
         encryptedBytes = new Uint8Array(buffer);
      }

      return encryptedBytes;
   }

   // Seperated out and made public for testing, normal callers should not use this function
   public async _createHeader(encryptedData: Uint8Array, additionalData: Uint8Array): Promise<Uint8Array> {
      if (!this._lastMac || !encryptedData || !additionalData) {
         throw new Error('Data not initialized');
      }

      const payloadBytes = encryptedData.byteLength + additionalData.byteLength;
      // Packer validates ranges as values are added
      const packer = new Packer(cc.HEADER_BYTES_6P, cc.MAC_BYTES);
      packer.ver = this.protocolVersion();
      packer.size = payloadBytes;

      const sodium = getSodium();
      const sk = await this._keyProvider.getSigningKey();
      const state = sodium.crypto_generichash_init(sk, cc.MAC_BYTES);
      sodium.crypto_generichash_update(state, new Uint8Array(packer.buffer, cc.MAC_BYTES));
      sodium.crypto_generichash_update(state, additionalData);
      sodium.crypto_generichash_update(state, encryptedData);
      sodium.crypto_generichash_update(state, this._lastMac);

      const mac = sodium.crypto_generichash_final(state, cc.MAC_BYTES);
      this._lastMac = mac;
      packer.offset = 0;
      packer.mac = mac;

      return packer.detach();
   }
}

type BlockData = {
   readonly mac: Uint8Array<ArrayBuffer>;
   readonly ver: number;
   readonly payloadSize: number;
   flags?: number;
   alg?: cc.CipherAlgs;
   iv?: Uint8Array<ArrayBuffer>;
   encryptedData?: Uint8Array<ArrayBuffer>;
   additionalData?: Uint8Array<ArrayBuffer>;
   keyCommitment?: Uint8Array<ArrayBuffer>;
};

export abstract class Decipher extends Ciphers {
   protected _blockNum;
   protected _blockData?: BlockData;
   private _decodeBlock0Result?: Promise<void>;

   protected constructor(keyProvider: KeyProvider, reader: BYOBStreamReader) {
      super(keyProvider, reader);

      this._blockNum = 1;
   }

   protected override _purge() {
      this._blockData = undefined;
      super._purge();
   }

   protected async _createAeadAd(baseAd: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
      return baseAd;
   }

   protected async _verifyEmptyReader(): Promise<void> {
      const [extra, done] = await this._reader.readAvailable(new ArrayBuffer(1));
      if (extra.byteLength !== 0 || !done) {
         throw new Error('Unexpected extra data');
      }
   }

   // When decryptBlock functions return an empty byte array, the
   // stream is done. It work like this because these function
   // retry until each block is read and decrypted (so only return
   // empty when done)

   // Helper that calls Block0 until ~success then BlockN
   async decryptBlock(): Promise<Uint8Array> {
      if ([CipherState.Initialized, CipherState.Block0Decoded].includes(this._state)) {
         return this.decryptBlock0();
      } else if (this._state === CipherState.Block0Done) {
         return this.decryptBlockN();
      } else {
         throw new Error(`Decipher invalid state ${this._state}`);
      }
   }

   // Password is a callback because we need to extract any hint from ciphertext first.
   // We also don't want the caller (web page) to show anything  extracted from
   // ciphertext until after it has been verified with the MAC. Order
   // of operations is:
   //
   // 1. Unpack values and checks userCred based MAC
   // 2. Decrypt hint if it exists
   // 3. Callback to get the password with the hint unpacked & validated hint
   // 4. Generate cipher keys using returned pwd + userCred
   // 5. Decrypt encrypted text using cipher key and addtional data
   // 6. Return cleat text bytes
   //
   public async decryptBlock0(): Promise<Uint8Array> {
      try {
         if (![CipherState.Initialized, CipherState.Block0Decoded].includes(this._state)) {
            throw new Error(`Decipher invalid state ${this._state}`);
         }

         // This does MAC check
         await this._decodeBlock0();
         if (
            !this._blockData?.alg ||
            !this._blockData.iv ||
            !this._blockData.encryptedData ||
            !this._blockData.additionalData
         ) {
            throw new Error('Data not initialized');
         }

         const ek = await this._keyProvider.getCipherKey(false);

         // Reject a wrong cipher key before it reaches the AEAD
         const keyCommitment = this._blockData.keyCommitment;
         if (this._blockData.ver >= cc.VERSION8) {
            const commitPresent = !!keyCommitment?.byteLength;
            if (commitPresent !== this._keyProvider.supportsCommitment) {
               throw new Error('Invalid key commitment presence');
            }
         }

         if (keyCommitment?.byteLength) {
            const expected = await this._keyProvider.getKeyCommitment();
            if (!getSodium().memcmp(expected, keyCommitment)) {
               throw new Error('Invalid key commitment');
            }
         }

         const aeadAd = await this._createAeadAd(this._blockData.additionalData);

         // Only block0 uses the root cipher key. Simplifies backward compat and is no less secure
         const decrypted = await Decipher._doDecrypt(
            this._blockData.alg,
            ek,
            this._blockData.iv,
            this._blockData.encryptedData,
            aeadAd,
         );

         this._state = CipherState.Block0Done;
         return decrypted;
      } catch (err) {
         this.errorState();
         logError(err);
         throw err;
      } finally {
         this._blockData = undefined;
      }
   }

   // Public only for testing. Subclasses implement _decodeBlock0Impl;
   // This wrapper exists to serialize concurrent callers.
   public async _decodeBlock0(): Promise<void> {
      if (![CipherState.Initialized, CipherState.Block0Decoded].includes(this._state)) {
         throw new Error(`Decode invalid state ${this._state}`);
      }
      if (this._decodeBlock0Result) {
         return this._decodeBlock0Result;
      } else {
         this._decodeBlock0Result = this._decodeBlock0Impl();
         return this._decodeBlock0Result;
      }
   }

   protected abstract _decodeBlock0Impl(): Promise<void>;
   public abstract decryptBlockN(): Promise<Uint8Array>;

   public async getCipherDataInfo(): Promise<CipherDataInfo> {
      // This does MAC check
      await this._decodeBlock0();
      if (!this._blockData?.alg || !this._blockData.iv) {
         throw new Error('Data not initialized');
      }

      return { ...this._keyProvider.getCipherDataInfo() };
   }

   protected static async _doDecrypt(
      alg: cc.CipherAlgs,
      key: Uint8Array,
      iv: Uint8Array,
      encrypted: Uint8Array,
      additionalData?: Uint8Array,
   ): Promise<Uint8Array> {
      const ivBytes = Number(Ciphers.algIVByteLength(alg));
      if (ivBytes !== iv.byteLength) {
         throw new Error(`incorrect iv length of: ${iv.byteLength}`);
      }
      if (key.every((b) => b === 0)) {
         throw new Error('Invalid key: all zero bytes');
      }

      let decrypted: Uint8Array;
      if (alg === 'X20-PLY') {
         /* console.log('dxcha encrypted', encrypted.byteLength, encrypted);
            console.log('dxcha additionalData', additionalData.byteLength, additionalData);
            console.log('dxcha iv', iv.byteLength, iv);
            console.log('dxcha keyBytes', keyBytes.byteLength, keyBytes);
         */
         try {
            decrypted = getSodium().crypto_aead_xchacha20poly1305_ietf_decrypt(
               null,
               encrypted,
               additionalData ?? null,
               iv,
               key,
            );
         } catch (err) {
            // Match behavior of Web Crytpo functions that throws limited DOMException
            const msg = err instanceof Error ? err.message : '';
            throw new DOMException(msg, 'OperationError ');
         }
      } else if (alg === 'AEGIS-256') {
         try {
            decrypted = getSodium().crypto_aead_aegis256_decrypt(null, encrypted, additionalData ?? null, iv, key);
         } catch (err) {
            // Match behavior of Web Crytpo functions that throws limited DOMException
            const msg = err instanceof Error ? err.message : '';
            throw new DOMException(msg, 'OperationError ');
         }
      } else {
         let dk: CryptoKey | undefined = await crypto.subtle.importKey('raw', getArrayBuffer(key), 'AES-GCM', false, [
            'decrypt',
         ]);

         const buffer = await crypto.subtle.decrypt(
            {
               name: alg,
               iv: getArrayBuffer(iv),
               additionalData: additionalData ? getArrayBuffer(additionalData) : new ArrayBuffer(0),
               tagLength: cc.AES_GCM_TAG_BYTES * 8,
            },
            dk,
            getArrayBuffer(encrypted),
         );

         dk = undefined;
         decrypted = new Uint8Array(buffer);
      }

      return decrypted;
   }
}

// Can handle version 6 and 7 (because the code is very similar)
export class DecipherV678 extends Decipher {
   /* V6/V7/V8 CipherData Layout. Tags are just notation, and are not actually in the
    * data stream. All encodings have one block0 instance followed by zero or
    * more blockN instances. V6 and V7 share this layout, the V7 changes were all
    * in key derivation. V8 added the two COMMIT fields.

      <Document>
         <Block0>
            <Header>
               MAC_BYTES - 32
               VER_BYTES - 2
               PAYLOAD_SIZE_BYTES - 3
            </Header>
            <Payload>
               <Additional Data>
                  FLAGS_BYTES - 1
                  ALG_BYTES - 2
                  IV_BYTES (variable) - [12, 24, 32]
                  SLT_BYTES - 16
                  IC_BYTES - 4
                  LPP_BYTES (packed lp and lpEnd) - 1
                  EHINT_LEN_BYTES - 1
                  EHINT_BYTES (variable) - [0-255]
                  COMMIT_LEN_BYTES (V8 only) - 1
                  COMMIT_BYTES (V8 only, variable) - [0, 32]
               </Additional Data>
               <Encrypted Data>
                  EDATA_BYTES (variable)
               </Encrypted Data>
            </Payload>
         </Block0>
         <BlockN>
            <Header>
               MAC_BYTES
               VER_BYTES
               PAYLOAD_SIZE_BYTES
            </Header>
            <Payload>
               <Additional Data>
                  FLAGS_BYTES
                  ALG_BYTES
                  IV_BYTES (variable)
               </Additional Data>
               <Encrypted Data>
                  EDATA_BYTES (variable)
               </Encrypted Data>
            </Payload>
         </BlockN>
         ...
      </Document>
   */

   private _header?: Uint8Array;
   private _lastMac?: Uint8Array;
   private _lastFlags: number;

   constructor(keyProvider: KeyProvider, reader: BYOBStreamReader, header?: Uint8Array) {
      super(keyProvider, reader);
      this._header = header;

      this._lastMac = new Uint8Array([0]);
      this._lastFlags = 0;
   }

   protected override _purge() {
      if (this._lastMac) {
         this._lastMac.fill(0);
         this._lastMac = undefined;
      }
      super._purge();
   }

   public override protocolVersion(): number {
      if (!this._blockData) {
         throw new Error('Invalid state, no block decoded');
      }
      return this._blockData.ver;
   }

   protected override async _createAeadAd(baseAd: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
      if (!this._blockData) {
         throw new Error('Data not initialized');
      }
      let aeadAd = baseAd;

      if (this._blockData.ver < cc.VERSION8) {
         const parts: Uint8Array<ArrayBuffer>[] = [baseAd];
         const extraKeyMaterial = this._keyProvider.getExtraKeyMaterial();
         if (extraKeyMaterial) {
            parts.push(extraKeyMaterial);
         }
         if (this._keyProvider.supportsCommitment) {
            parts.push(await this._keyProvider.getKeyCommitment());
         }
         aeadAd = concatArrays(parts);
      }

      return aeadAd;
   }

   private async _decodeHeader(header?: Uint8Array): Promise<boolean> {
      // Need to treat all values an UNTRUSTED since the signature has not yet been
      // validated.
      if (!header) {
         let done: boolean;
         [header, done] = await this._reader.readFill(new ArrayBuffer(cc.HEADER_BYTES_6P));
         if (header.byteLength > 0 && (done || header.byteLength < cc.HEADER_BYTES_6P)) {
            this._reader.cleanup();
            throw new Error('Missing cipher data header');
         }
      }

      // This signals successful completion of block reads
      if (header.byteLength === 0) {
         return true;
      }

      const extractor = new Extractor(header);

      // Order must be invariant (extractor validates sizes and ranges)
      const mac = extractor.mac;
      const ver = extractor.ver;
      if (ver !== cc.VERSION6 && ver !== cc.VERSION7 && ver !== cc.VERSION8) {
         throw new Error(`Invalid version of: ${ver}`);
      }
      const payloadSize = extractor.size;

      this._blockData = {
         mac: ensureArrayBuffer(mac),
         ver,
         payloadSize,
      };

      return false;
   }

   // Does not allow encoding with zero length encrypted text since that
   // is not needed.
   protected override async _decodeBlock0Impl(): Promise<void> {
      try {
         // This setups up _blockData
         await this._decodeHeader(this._header);
         if (!this._blockData) {
            throw new Error('Data not initialized');
         }

         // Need to treat all values an UNTRUSTED since the signature has not yet been
         // validated, Extractor does test each value for valid ranges as we unpack

         const [payload, done] = await this._reader.readFill(new ArrayBuffer(this._blockData.payloadSize));
         if (done) {
            this._reader.cleanup();
         }

         if (payload.byteLength !== this._blockData.payloadSize) {
            throw new Error(`Cipher data length mismatch1: ${payload.byteLength}`);
         }

         const extractor = new Extractor(payload);

         // Order must be invariant
         this._blockData.flags = extractor.flags;
         this._blockData.alg = extractor.alg;
         this._blockData.iv = extractor.iv;
         const slt = extractor.slt;
         const ic = extractor.ic;
         const [lp, lpEnd] = extractor.lpp();
         const encryptedHint = extractor.hint;
         if (this._blockData.ver >= cc.VERSION8) {
            this._blockData.keyCommitment = extractor.commit;
         }
         this._blockData.encryptedData = extractor.remainder('edata');

         // Since V4, additional data is the payload minus encrypted data
         this._blockData.additionalData = payload.subarray(
            0,
            extractor.offset - this._blockData.encryptedData.byteLength,
         );

         const cdInfo: CipherDataInfo = {
            ver: this._blockData.ver,
            alg: this._blockData.alg,
            ic,
            lp,
            lpEnd,
            slt,
         };

         this._keyProvider.setCipherDataInfo(cdInfo);

         // Avoiding the Doom Principle and verify signature before crypto operations.
         // Aka, check MAC as soon as possible after we have the signing key and data.
         await this._verifyMAC();

         // Checked after the MAC so this only ever reports on verified data
         if (this._blockData.ver >= cc.VERSION8) {
            const commitPresent = !!this._blockData.keyCommitment?.byteLength;
            if (commitPresent !== this._keyProvider.supportsCommitment) {
               throw new Error('Invalid key commitment presence');
            }
         }

         if (encryptedHint!.byteLength !== 0) {
            const [hk, hIV] = await this._keyProvider.getHintCipherKeyAndIV(this._blockData.iv);
            const hintBytes = await Decipher._doDecrypt(this._blockData.alg, hk, hIV, encryptedHint);

            // 0xFF cannot appear in UTF-8, so any such trailing bytes are padding
            let hintLen = hintBytes.byteLength;
            while (hintLen > 0 && hintBytes[hintLen - 1] === 0xff) {
               hintLen -= 1;
            }
            if (hintLen === 0) {
               throw new Error('Invalid hint padding');
            }
            this._keyProvider.setHint(new TextDecoder().decode(hintBytes.subarray(0, hintLen)));
         }

         this._state = CipherState.Block0Decoded;
         this._lastFlags = this._blockData.flags;
      } catch (err) {
         this.errorState();
         logError(err);
         throw err;
      } finally {
         this._header = undefined;
      }
   }

   public override async decryptBlockN(): Promise<Uint8Array> {
      try {
         if (this._state !== CipherState.Block0Done) {
            throw new Error(`Decipher invalid state ${this._state}`);
         }

         // This does MAC check
         await this._decodeBlockN();

         //@ts-expect-error
         if (this._state === CipherState.Finished) {
            // this is the signal that decryption is complete
            return new Uint8Array(0);
         }

         this._multiBlock = true;

         if (
            !this._blockData?.alg ||
            !this._blockData.iv ||
            !this._blockData.encryptedData ||
            !this._blockData.additionalData
         ) {
            throw new Error('Data not initialized');
         }

         const bk = await this._keyProvider.getBlockCipherKey(this._blockNum);
         this._blockNum += 1;

         const aeadAd = await this._createAeadAd(this._blockData.additionalData);

         const decrypted = await Decipher._doDecrypt(
            this._blockData.alg,
            bk,
            this._blockData.iv,
            this._blockData.encryptedData,
            aeadAd,
         );

         // Occurs when the last block was only present to mark termination (in v5+)
         if (decrypted.byteLength === 0) {
            await this._verifyEmptyReader();
            this.finishedState();
         }

         return decrypted;
      } catch (err) {
         this.errorState();
         logError(err);
         throw err;
      } finally {
         this._blockData = undefined;
      }
   }

   // Importers of CipherService should not need this function directly,
   // but it is public for unit testing. Does not allow encoding
   // with zero length encrypted text since that is not needed
   protected async _decodeBlockN(): Promise<void> {
      // Need to treat all values an UNTRUSTED since the signature has not yet been
      // validated, Extractor does test each value for valid ranges as we unpack
      try {
         // This setups up _blockData
         const done = await this._decodeHeader();
         if (done) {
            if (this._lastFlags !== 1) {
               throw new Error('Missing terminal data block');
            }
            this.finishedState();
            return;
         }

         // If we loaded more data, and lastFlags was 1 (change to bitfield someday)
         // we have an error
         if (this._lastFlags === 1) {
            throw new Error(`Extra data block ${this._state}`);
         }
         if (!this._blockData) {
            throw new Error('Data not initialized');
         }

         // Don't need to look at done for the fill since there will be another
         // call and the next header will report done.
         const [payload] = await this._reader.readFill(new ArrayBuffer(this._blockData.payloadSize));

         if (payload.byteLength !== this._blockData.payloadSize) {
            throw new Error(`Cipher data length mismatch2: ${payload.byteLength}`);
         }

         const extractor = new Extractor(payload);

         // Order must be invariant
         this._blockData.flags = extractor.flags;
         this._blockData.alg = extractor.alg;
         this._blockData.iv = extractor.iv;
         this._blockData.encryptedData = extractor.remainder('edata');

         // Since V4, additional data is the payload minus encrypted data
         this._blockData.additionalData = payload.subarray(
            0,
            extractor.offset - this._blockData.encryptedData.byteLength,
         );

         // Avoiding the Doom Principle and verify signature before crypto operations.
         // Aka, check MAC as soon as possible after we  have the signing key and data.
         await this._verifyMAC();

         // Only block0's algorithm and version are used. Later blocks carry the fields for a
         // potential future feature, so for now require them to match block0 to prevent
         // tampering. Checked after the MAC so this only ever reports on verified data.
         const cdInfo = this._keyProvider.getCipherDataInfo();
         if (this._blockData.alg !== cdInfo.alg) {
            throw new Error('Invalid block algorithm');
         }
         if (this._blockData.ver !== cdInfo.ver) {
            throw new Error('Invalid block version');
         }

         this._lastFlags = this._blockData.flags;
      } catch (err) {
         this.errorState();
         logError(err);
         throw err;
      }
   }

   private async _verifyMAC(): Promise<void> {
      if (
         !this._blockData?.payloadSize ||
         !this._blockData.ver ||
         !this._blockData.additionalData ||
         !this._blockData.encryptedData ||
         !this._blockData.mac ||
         !this._lastMac
      ) {
         throw new Error('Data not initialized');
      }

      const encVerBytes = numToBytes(this._blockData.ver, cc.VER_BYTES);
      const encSizeBytes = numToBytes(this._blockData.payloadSize, cc.PAYLOAD_SIZE_BYTES);

      const sodium = getSodium();
      const sk = await this._keyProvider.getSigningKey();
      const state = sodium.crypto_generichash_init(sk, cc.MAC_BYTES);
      sodium.crypto_generichash_update(state, encVerBytes);
      sodium.crypto_generichash_update(state, encSizeBytes);
      sodium.crypto_generichash_update(state, this._blockData.additionalData);
      sodium.crypto_generichash_update(state, this._blockData.encryptedData);
      sodium.crypto_generichash_update(state, this._lastMac);

      const testMac = sodium.crypto_generichash_final(state, cc.MAC_BYTES);
      const validMac: boolean = sodium.memcmp(this._blockData.mac, testMac);

      if (validMac) {
         this._lastMac = this._blockData.mac;
         return;
      }

      throw new Error('Invalid MAC signature');
   }
}

export class Extractor<T extends ArrayBufferLike> {
   private _encoded: Uint8Array<T>;
   private _offset: number;
   private _alg?: cc.CipherAlgs;

   constructor(encoded: Uint8Array<T>, offset: number = 0) {
      this._encoded = encoded;
      this._offset = offset;
   }

   extract(what: string, len: number): Uint8Array<T> {
      // some browsers complain about overruns (FF), while other don't (chrome),
      // so check explicitly
      if (this._offset + len > this._encoded.byteLength) {
         throw new Error(`Invalid ${what}, length: ${len}`);
      }

      const result = this._encoded.subarray(this._offset, this._offset + len);

      // shouldn't hit this given test above, but check anyway
      if (result.byteLength !== len) {
         throw new Error(`Invalid ${what}, length: ${result.byteLength}`);
      }

      this._offset += len;
      return result;
   }

   remainder(what: string): Uint8Array<T> {
      const result = this._encoded.subarray(this._offset);
      // happens if the encode data is not as long as expected
      if (result.byteLength === 0) {
         throw new Error(`Invalid ${what}, length: 0`);
      }

      this._offset += result.byteLength;
      return result;
   }

   get offset(): number {
      return this._offset;
   }

   get mac(): Uint8Array<T> {
      return this.extract('mac', cc.MAC_BYTES);
   }

   get alg(): cc.CipherAlgs {
      const algNum = bytesToNum(this.extract('alg', cc.ALG_BYTES));
      this._alg = Ciphers.algName(algNum);
      return this._alg;
   }

   get iv(): Uint8Array<T> {
      if (!this._alg) {
         throw new Error('iv length unknown, get extractor.alg first');
      }
      const ivBytes = Ciphers.algIVByteLength(this._alg);
      return this.extract('iv', ivBytes);
   }

   get slt(): Uint8Array<T> {
      return this.extract('slt', cc.SLT_BYTES);
   }

   get commit(): Uint8Array<T> {
      const commitLen = bytesToNum(this.extract('clen', cc.COMMIT_LEN_BYTES));
      if (commitLen !== 0 && commitLen !== cc.COMMIT_BYTES) {
         throw new Error(`Invalid commit length: ${commitLen}`);
      }
      return this.extract('commit', commitLen);
   }

   get ic(): number {
      const ic = bytesToNum(this.extract('ic', cc.IC_BYTES));
      // Sanity range only. The version-aware floor is applied by the key provider
      if (ic !== 0 && (ic < cc.ICOUNT_MIN_V4 || ic > cc.ICOUNT_MAX)) {
         throw new Error(`Invalid ic of: ${ic}`);
      }
      return ic;
   }

   lpp(): [lp: number, lpEnd: number] {
      const lpp = bytesToNum(this.extract('lpp', cc.LPP_BYTES));
      const lp = (lpp & 0x0f) + 1;
      const lpEnd = (lpp >> 4) + 1;
      // this can't happen... but... just check
      if (lpEnd < 1 || lpEnd > cc.LP_MAX) {
         throw new Error(`Invalid lpEnd of: ${lpEnd}`);
      }
      // only lp > lpEnd could happen
      if (lp < 1 || lp > lpEnd) {
         throw new Error(`Invalid lp of: ${lp}`);
      }
      return [lp, lpEnd];
   }

   get ver(): number {
      const ver = bytesToNum(this.extract('ver', cc.VER_BYTES));
      if (
         ver !== cc.VERSION1 &&
         ver !== cc.VERSION4 &&
         ver !== cc.VERSION5 &&
         ver !== cc.VERSION6 &&
         ver !== cc.VERSION7 &&
         ver !== cc.VERSION8
      ) {
         throw new Error(`Invalid version of: ${ver}`);
      }
      return ver;
   }

   get flags(): number {
      const flags = bytesToNum(this.extract('flags', cc.FLAGS_BYTES));
      // May be a bitfield later. For now, 0 or 1
      if (flags !== 0 && flags !== 1) {
         throw new Error(`Invalid flags of: ${flags}`);
      }
      return flags;
   }

   // Return zero length Array if no hint
   get hint(): Uint8Array<T> {
      const hintLen = bytesToNum(this.extract('hlen', cc.HINT_LEN_BYTES));
      const encryptedHint = this.extract('hint', hintLen);
      return encryptedHint;
   }

   get size(): number {
      const payloadSize = bytesToNum(this.extract('size', cc.PAYLOAD_SIZE_BYTES));
      if (payloadSize < cc.PAYLOAD_SIZE_MIN || payloadSize > cc.PAYLOAD_SIZE_MAX) {
         throw new Error(`Invalid payload size3: ${payloadSize}`);
      }
      return payloadSize;
   }
}

export class Packer {
   private _dest?: Uint8Array<ArrayBuffer>;
   private _offset: number;
   private _ivBytes?: number;

   constructor(maxSize: number, offset: number = 0) {
      this._dest = new Uint8Array(maxSize);
      this._offset = offset;
   }

   pack(what: string, data: Uint8Array) {
      if (!this._dest) {
         throw new Error('Packer was detached');
      }
      this._dest.set(data, this._offset);
      this._offset += data.byteLength;

      // happens if the encode data is not as long as expected
      if (this._offset > this._dest.byteLength) {
         throw new Error(`Invalid ${what}, length: ${data.byteLength}`);
      }
   }

   get offset(): number {
      return this._offset;
   }

   set offset(value: number) {
      if (!this._dest) {
         throw new Error('Packer was detached');
      }
      if (value > this._dest.byteLength) {
         throw new Error(`Invalid offset: ${value}`);
      }
      this._offset = value;
   }

   get buffer(): ArrayBuffer {
      if (!this._dest) {
         throw new Error('Packer was detached');
      }
      return this._dest.buffer;
   }

   trim(): Uint8Array<ArrayBuffer> {
      if (!this._dest) {
         throw new Error('Packer was detached');
      }
      return this._dest.subarray(0, this._offset);
   }

   detach(): Uint8Array<ArrayBuffer> {
      if (!this._dest) {
         throw new Error('Packer was detached');
      }
      const result = this._dest;
      this._dest = undefined;
      return result;
   }

   set data(data: Uint8Array) {
      this.pack('data', data);
   }

   set mac(sig: Uint8Array) {
      if (sig.byteLength !== cc.MAC_BYTES) {
         throw new Error(`Invalid MAC length: ${sig.byteLength}`);
      }
      this.pack('mac', sig);
   }

   set alg(algName: cc.CipherAlgs) {
      Ciphers.validateAlg(algName);
      this._ivBytes = Ciphers.algIVByteLength(algName);
      this.pack('alg', numToBytes(Ciphers.algId(algName), cc.ALG_BYTES));
   }

   set iv(iVect: Uint8Array) {
      if (!this._ivBytes) {
         throw new Error('IV length undefined, set packer.alg first');
      }
      if (this._ivBytes !== iVect.byteLength) {
         throw new Error(`Invalid IV length: ${iVect.byteLength}`);
      }
      this.pack('iv', iVect);
   }

   set slt(salt: Uint8Array) {
      if (salt.byteLength !== cc.SLT_BYTES) {
         throw new Error(`Invalid salt length: ${salt.byteLength}`);
      }
      this.pack('slt', salt);
   }

   set commit(keyCommitment: Uint8Array) {
      if (keyCommitment.byteLength !== 0 && keyCommitment.byteLength !== cc.COMMIT_BYTES) {
         throw new Error(`Invalid commit length: ${keyCommitment.byteLength}`);
      }
      this.pack('clen', numToBytes(keyCommitment.byteLength, cc.COMMIT_LEN_BYTES));
      this.pack('commit', keyCommitment);
   }

   set ic(iCount: number) {
      // Sanity range only. The version-aware floor is applied by the key provider
      if (iCount !== 0 && (iCount < cc.ICOUNT_MIN_V4 || iCount > cc.ICOUNT_MAX)) {
         throw new Error(`Invalid ic of: ${iCount}`);
      }
      this.pack('ic', numToBytes(iCount, cc.IC_BYTES));
   }

   lpp(lp: number, lpEnd: number) {
      if (lpEnd < 1 || lpEnd > cc.LP_MAX) {
         throw new Error(`Invalid lpEnd of: ${lpEnd}`);
      }
      if (lp < 1 || lp > lpEnd) {
         throw new Error(`Invalid lp of: ${lp}`);
      }
      let lpp = (lpEnd - 1) << 4;
      lpp += lp - 1;
      this.pack('lpp', numToBytes(lpp, cc.LPP_BYTES));
   }

   set ver(version: number) {
      if (
         version !== cc.VERSION1 &&
         version !== cc.VERSION4 &&
         version !== cc.VERSION5 &&
         version !== cc.VERSION6 &&
         version !== cc.VERSION7 &&
         version !== cc.VERSION8
      ) {
         throw new Error(`Invalid version of: ${version}`);
      }
      this.pack('ver', numToBytes(version, cc.VER_BYTES));
   }

   set flags(flags: number) {
      // May be a bitfield later. For now, 0 or 1
      if (flags !== 0 && flags !== 1) {
         throw new Error(`Invalid flags of: ${flags}`);
      }
      this.pack('flags', numToBytes(flags, cc.FLAGS_BYTES));
   }

   set hint(encHint: Uint8Array) {
      if (encHint.byteLength > cc.ENCRYPTED_HINT_MAX_BYTES) {
         throw new Error(`Invalid hint length: ${encHint.byteLength}`);
      }
      this.pack('hlen', numToBytes(encHint.byteLength, cc.HINT_LEN_BYTES));
      this.pack('hint', encHint);
   }

   set size(payloadSize: number) {
      if (payloadSize < cc.PAYLOAD_SIZE_MIN || payloadSize > cc.PAYLOAD_SIZE_MAX) {
         throw new Error(`Invalid payload size4: ${payloadSize}`);
      }
      this.pack('size', numToBytes(payloadSize, cc.PAYLOAD_SIZE_BYTES));
   }
}
